import _ from 'lodash';
import { t, Trans } from '@lingui/macro';
import ForwardIcon from '@material-ui/icons/Forward';
import { useEffect, useRef, useState } from 'react';
import FormSection from '../../form/FormSection';
import { getIpScopeListInDetailColumns, IP_SCOPES_FIELD } from './CollectorsAdminConstants';
import ListFormField from '../../form/ListFormField';
import { IP_RULE_FIELD } from '../../ip-form/IpFormConstants';
import { SORT_ASC } from '../../../utilities/FormUtilities';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import CollectorEditIpScopeForm from './CollectorEditIpScopeForm';
import IPAddressUtilities from '../../../utilities/IPAddressUtilities';
import CollectorMoveIpScopeForm from './CollectorMoveIpScopeForm';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';

const addButtonText = t`Add IP Scope`;
const emptyScopesGridText = t`This collector does not contain any IP scopes to monitor`;
const initialSortRules = [{ property: IP_RULE_FIELD, direction: SORT_ASC }];

export default function CollectorDetailIpScope({
    collector,
    transferrableCollectors,
    currentIpScopes,
    setIpScopesSelectionIsDirty,
    movedSubnetsRef,
    editScopeSubnetFromUrl,
    isDetailDirty,
    saveAndReloadDetail,
    reloadDetail,
}) {
    const transferrableCollectorsRef = useRef(transferrableCollectors);
    const currentIpScopesRef = useRef(currentIpScopes);
    const ipScopesListRef = useRef(null);
    const editTriggeredRef = useRef(false);
    const [subnetRangeCache, setSubnetRangeCache] = useState({});
    const [showMoveIpScopeDialog, setShowMoveIpScopeDialog] = useState(false);
    const [showSavePromptDialog, setShowSavePromptDialog] = useState(false);
    const [pendingMoveRecord, setPendingMoveRecord] = useState(undefined);
    const [selectedRecord, setSelectedRecord] = useState(undefined);

    transferrableCollectorsRef.current = transferrableCollectors;
    currentIpScopesRef.current = currentIpScopes;

    useEffect(() => {
        if (!editScopeSubnetFromUrl || editTriggeredRef.current) return;
        if (_.isEmpty(collector?.ipScopes)) return;
        if (!ipScopesListRef.current?.editRecord) return;

        editTriggeredRef.current = true;
        ipScopesListRef.current.editRecord((s) => _.get(s, 'value.subnet') === editScopeSubnetFromUrl);
    }, [editScopeSubnetFromUrl, collector]);

    if (_.isEmpty(collector)) {
        return null;
    }

    function getSubnetRange(subnet) {
        if (_.isUndefined(subnetRangeCache[subnet])) {
            subnetRangeCache[subnet] = IPAddressUtilities.getRangeBigIntegers(subnet);
        }

        return subnetRangeCache[subnet];
    }

    function hasEncompassingSubnetFunc(ipScope) {
        // Checking for encompassing subnets is expensive, so only do it if subnet transfer is possible (i.e. a
        // destination collector is available).
        const hasDestinationCollector = transferrableCollectorsRef.current.length > 0;
        if (hasDestinationCollector) {
            const scopes = _.filter(currentIpScopesRef.current, (item) => item._id !== ipScope._id);
            const currSubnet = _.get(ipScope, 'value.subnet');
            return _.some(scopes, (s) => {
                const subnet = _.get(s, 'value.subnet');
                return IPAddressUtilities.rangeEncompasses(getSubnetRange(currSubnet), getSubnetRange(subnet));
            });
        }

        return false;
    }

    function getTransferIcon(ipScope) {
        const hasEncompassingSubnet = hasEncompassingSubnetFunc(ipScope);
        const hasDestinationCollector = transferrableCollectorsRef.current.length > 0;

        if (!hasDestinationCollector) {
            return null;
        }

        if (hasEncompassingSubnet) {
            return <ForwardIcon color={'disabled'} />;
        }

        return <ForwardIcon />;
    }

    function getTransferClickHandler(ipScope) {
        const hasEncompassingSubnet = hasEncompassingSubnetFunc(ipScope);

        if (hasEncompassingSubnet) {
            return null;
        }

        return moveIpScope(ipScope);
    }

    const moveIpScope = (record) => {
        // Match the portal/ui saveDirtyCheck behavior: if there are unsaved changes, prompt the user
        // to save (and reload the detail) before opening the transfer dialog.
        if (_.isFunction(isDetailDirty) && isDetailDirty()) {
            setPendingMoveRecord(record);
            setShowSavePromptDialog(true);
            return;
        }
        openMoveDialog(record);
    };

    const openMoveDialog = (record) => {
        setSelectedRecord(record);
        setShowMoveIpScopeDialog(true);
    };

    const handleSavePromptConfirm = async () => {
        if (!_.isFunction(saveAndReloadDetail)) {
            openMoveDialog(pendingMoveRecord);
            setPendingMoveRecord(undefined);
            return;
        }
        const saved = await saveAndReloadDetail();
        if (saved) {
            openMoveDialog(pendingMoveRecord);
        }
        setPendingMoveRecord(undefined);
    };

    const handleSavePromptClose = () => {
        setPendingMoveRecord(undefined);
    };

    const handleMoveSuccess = ({ movedSubnet, childSubnets }) => {
        const removedSubnets = new Set([movedSubnet, ...childSubnets]);
        if (ipScopesListRef.current) {
            ipScopesListRef.current.removeRecords((scope) => removedSubnets.has(_.get(scope, 'value.subnet')));
        }
        if (movedSubnetsRef?.current) {
            removedSubnets.forEach((s) => movedSubnetsRef.current.add(s));
        }
        // Re-query the detail so the local form state matches the server after the transfer (portal/ui _queryDetail).
        if (_.isFunction(reloadDetail)) {
            reloadDetail();
        }
    };

    const forwardColumn = [
        {
            type: COLUMN_TYPES.ICON,
            title: '',
            width: 30,
            icons: [
                {
                    getIcon: (r) => getTransferIcon(r),
                    getTooltip: (r) =>
                        hasEncompassingSubnetFunc(r)
                            ? t`This scope can only be transferred with its encompassing subnet`
                            : t`Transfer this IP scope to another collector`,
                    clickHandler: (r) => getTransferClickHandler(r),
                },
            ],
        },
    ];

    const handleEditIpScopeFromLink = (record) => {
        if (ipScopesListRef.current?.editRecord) {
            ipScopesListRef.current.editRecord(
                (scope) => _.get(scope, 'value.subnet') === _.get(record, 'value.subnet')
            );
        }
    };

    return (
        <FormSection title={t`IP Scopes`} addSpacer>
            <ListFormField
                ref={ipScopesListRef}
                fieldName={IP_SCOPES_FIELD}
                dbSelected={_.get(collector, IP_SCOPES_FIELD)}
                addButtonText={addButtonText}
                gridColumns={[...forwardColumn, ...getIpScopeListInDetailColumns(handleEditIpScopeFromLink)]}
                emptyGridText={emptyScopesGridText}
                formComponent={
                    <CollectorEditIpScopeForm
                        associatedCollector={collector}
                        setSubnetRangeCache={setSubnetRangeCache}
                    />
                }
                selectedListSorter={initialSortRules}
                setSelectionIsDirty={setIpScopesSelectionIsDirty}
                showGridToolbar
                showSearch
                visibleRows={8}
            />
            <CollectorMoveIpScopeForm
                associatedCollector={collector}
                selectedRecord={selectedRecord}
                setSelectedRecord={setSelectedRecord}
                setSubnetRangeCache={setSubnetRangeCache}
                transferrableCollectors={transferrableCollectors}
                open={showMoveIpScopeDialog}
                setOpen={setShowMoveIpScopeDialog}
                onMoveSuccess={handleMoveSuccess}
            />
            <NgpTwoButtonDialog
                title={<Trans>Save Changes</Trans>}
                confirmButtonText={<Trans>Save and Continue</Trans>}
                cancelButtonText={<Trans>Cancel</Trans>}
                open={showSavePromptDialog}
                setOpen={setShowSavePromptDialog}
                onConfirm={handleSavePromptConfirm}
                onCloseFunc={handleSavePromptClose}
            >
                <Trans>
                    This collector has unsaved changes that must be saved before transferring an IP scope. Save and
                    continue?
                </Trans>
            </NgpTwoButtonDialog>
        </FormSection>
    );
}
